import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Moon, Pill, Calendar, HelpCircle, CalendarX, Info, Database, ShieldOff, Sparkles, Settings, Shield, Baby, Heart } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import type { HormonalStatus } from '@/types/user';

// UI selection values - these map to database values
export type MenstrualStatus = 
  | 'regular_cycle' 
  | 'irregular_cycle' 
  | 'hormonal_birth_control' 
  | 'menopause'
  | 'pregnancy'
  | 'breastfeeding'
  | 'no_periods_other'
  | 'prefer_not_to_say';

interface OnboardingSlideHormonalStatusProps {
  onNext: () => void;
  isCompleting?: boolean;
  onMenstrualStatusSelect?: (status: MenstrualStatus) => void;
}

interface StatusOption {
  value: MenstrualStatus;
  label: string;
  icon: typeof Calendar;
  description: string;
  dbValue: HormonalStatus;
  showCyclePhase?: boolean; // Whether to show cycle phase follow-up
}

const statusOptions: StatusOption[] = [
  {
    value: 'regular_cycle',
    label: 'Yes – regular cycle',
    icon: Calendar,
    description: 'I have a predictable monthly cycle',
    dbValue: 'menstrual_cycle',
    showCyclePhase: true,
  },
  {
    value: 'irregular_cycle',
    label: 'Yes – irregular cycle',
    icon: Moon,
    description: 'My cycle is unpredictable or varies',
    dbValue: 'menstrual_cycle_irregular',
    showCyclePhase: true,
  },
  {
    value: 'hormonal_birth_control',
    label: 'Hormonal birth control',
    icon: Pill,
    description: 'Pills, IUD, implant, or injection',
    dbValue: 'hormonal_birth_control',
  },
  {
    value: 'pregnancy',
    label: 'Currently pregnant',
    icon: Baby,
    description: 'We\'ll recommend pregnancy-safe ingredients',
    dbValue: 'pregnancy',
  },
  {
    value: 'breastfeeding',
    label: 'Breastfeeding',
    icon: Heart,
    description: 'We\'ll recommend breastfeeding-safe ingredients',
    dbValue: 'breastfeeding',
  },
  {
    value: 'menopause',
    label: 'Perimenopause or menopause',
    icon: CalendarX,
    description: 'Tailored for hormonal skin changes',
    dbValue: 'menopause',
  },
  {
    value: 'no_periods_other',
    label: 'No periods (other reasons)',
    icon: CalendarX,
    description: 'Medical reasons or other',
    dbValue: 'no_periods',
  },
  {
    value: 'prefer_not_to_say',
    label: 'Prefer not to say',
    icon: HelpCircle,
    description: 'Continue without this information',
    dbValue: 'not_applicable',
  },
];

// Messages shown after selection
const statusMessages: Record<MenstrualStatus, string | null> = {
  regular_cycle: null,
  irregular_cycle: null,
  hormonal_birth_control: "Hormonal birth control influences skin differently. We'll personalise your insights based on your hormone-regulated pattern.",
  pregnancy: "We'll prioritise pregnancy-safe ingredients and avoid anything that could be harmful.",
  breastfeeding: "We'll recommend ingredients safe for breastfeeding and prioritise gentle formulations.",
  menopause: "We'll focus on ingredients that support mature skin during hormonal transitions.",
  no_periods_other: "No problem — we'll personalise your FaceScore without cycle data.",
  prefer_not_to_say: null,
};

export const OnboardingSlideHormonalStatus = ({ 
  onNext, 
  isCompleting,
  onMenstrualStatusSelect 
}: OnboardingSlideHormonalStatusProps) => {
  const [selectedStatus, setSelectedStatus] = useState<MenstrualStatus | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSelection = (status: MenstrualStatus) => {
    setSelectedStatus(status);
    onMenstrualStatusSelect?.(status);
  };

  const handleContinue = async () => {
    if (!selectedStatus) {
      toast.error('Please select an option');
      return;
    }

    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // User not authenticated yet, just continue to next slide
        // They can update hormonal status later in Settings
        onNext();
        return;
      }

      const option = statusOptions.find(o => o.value === selectedStatus);
      const dbValue = option?.dbValue || 'not_applicable';

      const { error } = await supabase
        .from('profiles')
        .update({
          hormonal_status: dbValue,
          hormonal_data_updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;

      toast.success('Hormonal health preferences saved');
      onNext();
    } catch (error) {
      console.error('Error saving hormonal status:', error);
      toast.error('Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  // Check if we should show a reassuring message
  const message = selectedStatus ? statusMessages[selectedStatus] : null;

  return (
    <Card className="shadow-card border-0 bg-gradient-card">
      <CardContent className="p-6 md:p-8 flex flex-col">
        <div className="text-center space-y-3 mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center mb-3 shadow-glow animate-scale-in mx-auto">
            <Calendar className="w-8 h-8 text-primary-foreground" />
          </div>
          <div className="flex items-center justify-center gap-2 animate-fade-in">
            <h2 className="text-xl md:text-2xl font-semibold">
              Do you have a menstrual cycle?
            </h2>
            <Dialog>
              <DialogTrigger asChild>
                <button className="p-1 rounded-full hover:bg-muted transition-colors">
                  <Info className="w-5 h-5 text-muted-foreground hover:text-primary" />
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    Why We Ask About Hormonal Health
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                  <p className="text-muted-foreground">
                    Hormones have a direct, measurable impact on your skin. Understanding your hormonal patterns helps us provide more accurate insights.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                      <Sparkles className="w-4 h-4 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Better Recommendations</p>
                        <p className="text-xs text-muted-foreground">We adjust ingredient and product suggestions based on where you are in your cycle.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                      <Database className="w-4 h-4 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">What We Store</p>
                        <p className="text-xs text-muted-foreground">Only your hormonal status, cycle phase (if shared), and last update timestamp.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                      <ShieldOff className="w-4 h-4 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">What We Don't Store</p>
                        <p className="text-xs text-muted-foreground">Period dates, medical history, or health conditions beyond skincare relevance.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                      <Settings className="w-4 h-4 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Your Control</p>
                        <p className="text-xs text-muted-foreground">Edit or delete anytime in Settings. "Prefer not to say" is always available.</p>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground border-t pt-3">
                    Hormones affect oil production, hydration, sensitivity, and pigmentation. Tracking these patterns helps us give you smarter, more personalised skincare advice.
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
            This helps us improve accuracy of your FaceScore by accounting for hormonal skin changes. Your answer is private and never shared.
          </p>
        </div>

        <div className="space-y-3 mb-6">
          {statusOptions.map((option, index) => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                onClick={() => handleSelection(option.value)}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left animate-fade-in ${
                  selectedStatus === option.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 bg-background'
                }`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${
                    selectedStatus === option.value ? 'bg-primary text-primary-foreground' : 'bg-muted'
                  }`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm mb-1">{option.label}</h3>
                    <p className="text-xs text-muted-foreground">{option.description}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedStatus === option.value
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground/30'
                  }`}>
                    {selectedStatus === option.value && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Reassuring message for certain selections */}
        {message && (
          <div className="mb-6 p-4 rounded-lg bg-primary/5 border border-primary/20 animate-fade-in">
            <p className="text-sm text-foreground/80">
              {message}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button
            onClick={handleContinue}
            disabled={!selectedStatus || isSaving || isCompleting}
            className="w-full bg-gradient-primary hover:opacity-90"
            size="lg"
          >
            {isSaving ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
